import { ChangeDetectionStrategy, Component, output, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, map, of, skip, Subject, switchMap, timer } from 'rxjs';

import { ButtonIconComponent } from '@blaze-shared/shared-ui';

type SearchTrigger = {
  value: string;
  immediate: boolean;
};

@UntilDestroy()
@Component({
  selector: 'be-conversations-search',
  templateUrl: './conversations-search.component.html',
  styleUrls: ['./conversations-search.component.scss'],
  imports: [MatIcon, ButtonIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationsSearchComponent {
  conversationsSearch = output<string>();

  private readonly searchTrigger$ = new Subject<SearchTrigger>();

  inputFocus = signal(false);

  constructor() {
    this.searchTrigger$
      .pipe(
        skip(1), // Skip the initial value after init to prevent unnecessary reload
        map(({ value, immediate }) => ({ value: value.trim(), immediate })),
        distinctUntilChanged(),
        filter(({ value }) => value.length > 2 || value === ''),
        switchMap(({ value, immediate }) =>
          immediate ? of(value) : timer(2000).pipe(map(() => value)),
        ),
        untilDestroyed(this),
      )
      .subscribe((value) => this.conversationsSearch.emit(value));
  }

  onInputChange(value: string, immediate: boolean): void {
    this.searchTrigger$.next({ value, immediate });
  }

  clear(input: HTMLInputElement): void {
    this.searchTrigger$.next({ value: '', immediate: true });
    input.value = '';
  }
}
